import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  SimpleGrid,
  useColorMode,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  HStack,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { WarningIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { brandConfig, getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

interface SupplyRoute {
  name: string;
  from: string;
  to: string;
  transitDays: string;
  riskLevel: "low" | "medium" | "high";
  chokePoints: string[];
  description: string;
  pros: string[];
  cons: string[];
}

const routes: SupplyRoute[] = [
  {
    name: "US Pacific Route",
    from: "Houston/LA (USA)",
    to: "Brisbane/Japan",
    transitDays: "10-14 days",
    riskLevel: "low",
    chokePoints: [],
    description:
      "Direct Pacific crossing from US West Coast or Gulf Coast refineries. Completely bypasses Hormuz and Malacca straits.",
    pros: [
      "Zero chokepoint risk -- fully independent of Middle East",
      "US has ramped up oil exports significantly",
      "Trump admin incentivising Japan to buy American energy",
      "Phillips 66 Ferndale (Washington) is closest US refinery to Japan (~10 days)",
    ],
    cons: [
      "Higher shipping cost than Singapore-refined fuel historically",
      "Longer transit than SE Asia sources for Australia",
      "Dependent on US export policy and pricing",
    ],
  },
  {
    name: "Cape of Good Hope Route",
    from: "Middle East / West Africa",
    to: "Brisbane/Japan",
    transitDays: "25-32 days",
    riskLevel: "low",
    chokePoints: [],
    description:
      "Routes around the southern tip of Africa, bypassing both Hormuz and Malacca entirely. True independence from all chokepoints.",
    pros: [
      "Bypasses every single chokepoint",
      "Can source from West Africa (Nigeria, Angola) which produces light sweet crude",
      "Immune to Middle East disruption",
    ],
    cons: [
      "25-32 days transit -- very expensive in shipping costs",
      "Requires much larger floating storage to maintain supply continuity",
      "Fuel costs significantly higher per litre",
    ],
  },
  {
    name: "Russia ESPO Pipeline",
    from: "Kozmino Port (Russia)",
    to: "Japan",
    transitDays: "2-3 days",
    riskLevel: "medium",
    chokePoints: [],
    description:
      "Eastern Siberia-Pacific Ocean pipeline terminates at Kozmino Port near Vladivostok, only ~900km from Japan. Physically the closest alternative.",
    pros: [
      "Shortest possible transit to Japan (~900km)",
      "Pipeline is already built and operational",
      "Pre-war volumes were ~130,000 bbl/day to Japan",
    ],
    cons: [
      "Politically fraught due to Ukraine war sanctions",
      "Volumes significantly reduced under international pressure",
      "Sanctions risk for companies involved",
    ],
  },
  {
    name: "West Africa Route",
    from: "Nigeria / Angola",
    to: "Brisbane/Japan",
    transitDays: "~25 days",
    riskLevel: "medium",
    chokePoints: ["Gulf of Guinea (piracy risk)"],
    description:
      "West African producers (Nigeria, Angola) export light sweet crude. Route goes around the Cape of Good Hope to reach Asia-Pacific.",
    pros: [
      "Produces high-quality light sweet crude",
      "Bypasses Hormuz entirely",
      "Existing export infrastructure at Bonny Terminal (Nigeria)",
    ],
    cons: [
      "Long transit (~25 days to AU)",
      "Gulf of Guinea piracy risk",
      "Political instability in producing countries",
    ],
  },
];

const riskColor = (risk: string) => {
  switch (risk) {
    case "high": return "red";
    case "medium": return "orange";
    case "low": return "green";
    default: return "gray";
  }
};

const AlternativeSupplyRoutes: React.FC = () => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const textPrimary = getColor("text.primary", colorMode);
  const surfaceBg = brandConfig.colors.background.surface;
  const borderLight = brandConfig.colors.border.light;
  const textSecondary = brandConfig.colors.text.secondary;

  return (
    <Box bg={bgMain} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={10} flex="1">
        <VStack spacing={6} align="stretch">
          <Breadcrumb fontSize="sm" color={textSecondary}>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to="/energy-research">
                Energy Research
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink>Alternative Supply Routes</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Heading size="lg" color="#dc2626">
            Alternative Supply Routes
          </Heading>
          <Text color={textSecondary} fontSize="sm">
            If the Strait of Hormuz is blocked, how else can Australia and Japan source fuel?
            These are the bypass routes -- ranked by risk and transit time.
          </Text>

          <SimpleGrid columns={{ base: 1 }} spacing={5}>
            {routes.map((route) => (
              <Box
                key={route.name}
                p={6}
                bg={surfaceBg}
                borderRadius="lg"
                border="1px solid"
                borderColor={borderLight}
              >
                <VStack align="start" spacing={3}>
                  <HStack spacing={3}>
                    <Heading size="md" color="#dc2626">
                      {route.name}
                    </Heading>
                    <Badge colorScheme={riskColor(route.riskLevel)} fontSize="xs">
                      {route.riskLevel} risk
                    </Badge>
                  </HStack>

                  <HStack spacing={6} flexWrap="wrap">
                    <Text fontSize="sm" color={textSecondary}>
                      <strong>From:</strong> {route.from}
                    </Text>
                    <Text fontSize="sm" color={textSecondary}>
                      <strong>To:</strong> {route.to}
                    </Text>
                    <Text fontSize="sm" color={textSecondary}>
                      <strong>Transit:</strong> {route.transitDays}
                    </Text>
                  </HStack>

                  <Text fontSize="sm" color={textPrimary}>
                    {route.description}
                  </Text>

                  {route.chokePoints.length > 0 && (
                    <Text fontSize="sm" color="orange.400">
                      Chokepoints: {route.chokePoints.join(", ")}
                    </Text>
                  )}
                  {route.chokePoints.length === 0 && (
                    <Text fontSize="sm" color="green.400">
                      No chokepoints on this route
                    </Text>
                  )}

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                    <Box>
                      <Text fontSize="xs" color="green.400" fontWeight="bold" mb={1}>
                        PROS
                      </Text>
                      <List spacing={1}>
                        {route.pros.map((pro, i) => (
                          <ListItem key={i} fontSize="sm" color={textPrimary}>
                            <ListIcon as={CheckCircleIcon} color="green.400" />
                            {pro}
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="red.400" fontWeight="bold" mb={1}>
                        CONS
                      </Text>
                      <List spacing={1}>
                        {route.cons.map((con, i) => (
                          <ListItem key={i} fontSize="sm" color={textPrimary}>
                            <ListIcon as={WarningIcon} color="red.400" />
                            {con}
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </SimpleGrid>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default AlternativeSupplyRoutes;
